import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ListResponse } from "../Api";
import { useSearchParams } from "react-router-dom";

/*
testar: mudar coisa numa pagina, depois ir pra outra página e ver se resetou
*/

type SearchFunction<T> = (
  searchTerm: string,
  page: string
) => Promise<ListResponse<T>>;
type EntitySearchProps<T> = {
  searchFunction: SearchFunction<T>;
  setEntities: (entities: T[] | null) => void;
  placeholder?: string | null;
  onSearch?: () => void;
};
const EntitySearch = <T,>({
  searchFunction,
  setEntities,
  placeholder = null,
  onSearch = () => {},
}: EntitySearchProps<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q");
  const p = searchParams.get("p");

  const timerRef = useRef<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(q ?? "");
  const [lastPage, setLastPage] = useState("1");

  useEffect(() => {
    setSearchTerm(q ?? "");
    setEntities(null);
    setLoading(true);
    searchFunction(q ?? "", p ?? "1").then(({ lastPage, data }) => [
      setLastPage(lastPage.toString()),
      setEntities(data),
      setLoading(false),
    ]);
  }, [q, p, searchFunction, setEntities]);

  const nextPage = () => {
    setSearchParams((params) => {
      params.set("p", (parseInt(params.get("p") ?? "1") + 1).toString());
      return params;
    });
  };

  const previousPage = () => {
    setSearchParams((params) => {
      params.get("p") === "2"
        ? params.delete("p")
        : params.set("p", (parseInt(params.get("p")!) - 1).toString());
      return params;
    });
  };

  const search = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      onSearch();
      setSearchParams((params) => {
        searchTerm ? params.set("q", searchTerm) : params.delete("q");
        params.delete("p");
        return params;
      });
    }, 500);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSearchParams((params) => {
      params.delete("q");
      params.delete("p");
      return params;
    });
  };

  return (
    <div className="flex gap-4">
      <button
        className="text-xl disabled:text-gray-200 hover:text-sky-500"
        disabled={loading || !searchParams.get("p")}
        onClick={previousPage}
      >
        <FontAwesomeIcon icon="circle-chevron-left" />
      </button>

      <input
        className="border rounded-full py-1 px-3 text-sm flex-1 pr-8"
        placeholder={placeholder ?? "Buscar"}
        value={searchTerm}
        onChange={(event) => search(event.target.value)}
      />

      {searchTerm && (
        <button
          className="text-gray-400 rounded-full justify-center -ml-12 px-2"
          onClick={clearSearch}
        >
          <FontAwesomeIcon icon="circle-xmark" />
        </button>
      )}

      <button
        className="text-xl disabled:text-gray-200 hover:text-sky-500"
        disabled={
          loading ||
          lastPage === "0" ||
          lastPage === "1" ||
          searchParams.get("p") === lastPage
        }
        onClick={nextPage}
      >
        <FontAwesomeIcon icon="circle-chevron-right" />
      </button>
    </div>
  );
};

export default EntitySearch;
